import { useContext, type ReactNode } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  BellRingIcon,
  ExternalLinkIcon,
  MessagesSquare,
  TriangleAlertIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router";

import { useUserSheet } from "./userSheetContext";
import { UserSheetHeaderLocation } from "./UserSheetHeaderLocation";

import { AgenticScorePill, Center, Column, Row, Text } from "@/components";
import { InternalSourceBadge } from "@/components/InternalSourceBadge";
import { StevieCallIndicators } from "@jobs/pipeline/shared/StevieCallIndicators";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { SheetScopeContext } from "@/contexts/SheetScopeContext";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidacySheetData } from "@/hooks/queries/useGetCandidacySheetData";
import { useOverrideAgenticScore } from "@/hooks/mutations/useOverrideAgenticScore";
import { rateCandidacyMutationKey } from "@/hooks/mutations/useRateCandidacy";
import { useSetCandidacyInfo } from "@/hooks/sets/useSetCandidacyInfo";
import { agenticBucketToScore, agenticScoreToBucket } from "@/lib/agenticScore";
import { formatting } from "@/lib/formatting";
import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { AgenticScoreBucket } from "@/types";

// The sticky identity row: avatar, name, score, and actions. The contact and
// metric rows live in UserSheetHeaderDetails, which scrolls with the tab
// content underneath this row.
export const UserSheetHeader = ({
  actions,
  agenticScore,
  agenticScoreJustification,
  fallbackName,
  isAgenticScoreLoading = false,
  moreActions,
  nameLinkTo,
}: {
  actions?: ReactNode;
  agenticScore?: number | null;
  // Overrides the candidacy's justification for callers whose score comes from
  // elsewhere (search interests).
  agenticScoreJustification?: string | null;
  fallbackName?: string;
  // Callers whose score is computed async (e.g. search interests) can show
  // the pill's spinner while the backend is still rating.
  isAgenticScoreLoading?: boolean;
  moreActions?: ReactNode;
  // When set, the name opens this route in a new tab. Used by hosts that embed
  // the sheet read-only (Application Review) so a reviewer can reach the full
  // sheet, and its status controls, without losing their place in the queue.
  nameLinkTo?: string;
}) => {
  /* Context */
  const {
    candidacyId,
    hasCommunications,
    isCommunicationsOpen,
    jobId,
    openCommunications,
    workerId,
  } = useUserSheet();
  const sheetScopeContext = useContext(SheetScopeContext);
  const location = useLocation();

  /* Database CRUD Operations */
  // Read
  const profile = useGetCandidacySheetData({ jobId, userId: workerId }).data;
  const candidacyInfo = useGetCandidacyInfo({
    candidacyId: candidacyId ?? "",
  }).data;
  // Update
  const { mutate: overrideAgenticScore, isPending: isOverridingAgenticScore } =
    useOverrideAgenticScore();
  const isRatingCandidacy =
    useIsMutating({
      mutationKey: rateCandidacyMutationKey,
      predicate: (mutation) => {
        const variables = mutation.state.variables as
          | { candidacyId?: string }
          | undefined;
        return variables?.candidacyId === candidacyId;
      },
    }) > 0;
  const setCandidacyInfo = useSetCandidacyInfo();

  const successToast = useSuccessToast();

  /* Helpers */
  // The comms rail pushes the header in; tab content shifts by a different
  // amount, so this is derived here rather than shared through context.
  const headerPaddingClassName = isCommunicationsOpen ? "pl-10 pr-6" : "px-6";
  const firstName = profile?.profile.firstName;
  const lastName = profile?.profile.lastName;
  const fullName = formatting.fullName(
    firstName,
    lastName,
    fallbackName ?? "Candidate",
  );
  const permanentStatus = profile?.permanentStatus;

  /* Handlers */
  const handleOverrideAgenticRating = (
    bucket: AgenticScoreBucket,
    justification: string,
  ) => {
    if (!candidacyId) return;
    const toScore = agenticBucketToScore[bucket];

    mixpanelUtils.trackFormSubmission(
      "Agentic Rating Override",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      {
        new_score: toScore,
        companyName: sheetScopeContext?.companyName,
        jobTitle: sheetScopeContext?.job?.title,
      },
    );

    overrideAgenticScore(
      { candidacyId, justification, toScore },
      {
        onSuccess: (candidacyAgenticScoreChange) => {
          setCandidacyInfo({
            candidacyId,
            agenticScore: candidacyAgenticScoreChange.toScore,
            agenticScoreByUserId: candidacyAgenticScoreChange.userId,
            agenticScoreJustification:
              candidacyAgenticScoreChange.justification,
          });
          successToast({ description: "Agentic rating updated!" });
        },
      },
    );
  };

  /* Render */
  const renderName = () => {
    const nameText = (
      <Text className="text-xl font-semibold tracking-tight text-slate-900">
        {fullName}
      </Text>
    );
    if (!nameLinkTo) return nameText;

    return (
      <Link
        className="group/name-link flex min-w-0 items-center gap-1.5 hover:underline"
        onClick={() => {
          mixpanelUtils.trackButtonClick(
            "Open candidate sheet in new tab",
            getScreenFromPathname(location.pathname),
          );
        }}
        rel="noreferrer"
        target="_blank"
        title="Open full candidate sheet in a new tab"
        to={nameLinkTo}
      >
        {nameText}
        <ExternalLinkIcon className="h-4 w-4 shrink-0 text-slate-400 group-hover/name-link:text-slate-600" />
      </Link>
    );
  };

  const renderIdentity = () => {
    return (
      <Row className="min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-2">
        <Center className="h-[52px] w-[52px] shrink-0 rounded-full bg-primary-200">
          <Text className="text-sm font-semibold text-primary-900">
            {formatting.initials(firstName, lastName)}
          </Text>
        </Center>
        <Column className="min-w-0 flex-1 justify-center gap-1">
          <Row className="min-w-0 flex-wrap items-center gap-x-2 gap-y-1 pb-1 pt-1">
            {renderName()}
            <AgenticScorePill
              bucket={agenticScoreToBucket(agenticScore)}
              isHumanRated={!!candidacyInfo?.agenticScoreByUserId}
              isLoading={
                isAgenticScoreLoading ||
                isOverridingAgenticScore ||
                isRatingCandidacy
              }
              justification={
                agenticScoreJustification ??
                candidacyInfo?.agenticScoreJustification ??
                undefined
              }
              // Read-only hosts (Application Review) still allow overrides: the
              // rating is the reviewer's call to make, unlike profile edits.
              onSubmit={candidacyId ? handleOverrideAgenticRating : undefined}
            />
            <InternalSourceBadge source={candidacyInfo?.source ?? null} />
            <StevieCallIndicators
              currentScore={agenticScore ?? null}
              elevatedFrom={candidacyInfo?.elevatedFrom ?? null}
              stevieCallDuration={candidacyInfo?.stevieCallDuration ?? null}
            />
            {candidacyInfo?.sallyNudgedAt ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Row className="h-6 w-6 items-center justify-center text-primary-700">
                      <BellRingIcon className="h-4 w-4" />
                    </Row>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72" side="top">
                    <Text className="text-xs font-normal text-slate-700">
                      Sally nudged this candidate{" "}
                      {formatDistanceToNow(
                        new Date(candidacyInfo.sallyNudgedAt),
                        {
                          addSuffix: true,
                        },
                      )}{" "}
                      that the team is trying to reach them.
                    </Text>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {permanentStatus ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Row className="h-6 w-6 items-center justify-center text-red-600">
                      <TriangleAlertIcon className="h-4 w-4 fill-red-100" />
                    </Row>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-72" side="top">
                    <Text className="text-xs font-normal text-slate-700">
                      {permanentStatus}
                    </Text>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
            {moreActions}
          </Row>
          <UserSheetHeaderLocation />
        </Column>
      </Row>
    );
  };

  const render = () => {
    return (
      <Row
        className={cn(
          // Named group so hover-revealed header actions (moreActions) can key off the whole header row.
          "group/sheet-header items-start justify-between gap-6 pt-8",
          headerPaddingClassName,
        )}
      >
        {renderIdentity()}
        <Row className="shrink-0 items-center">
          <Row className="items-center gap-3 pr-3">{actions}</Row>
          {hasCommunications && !isCommunicationsOpen ? (
            <Button
              className="-mr-6 h-[52px] w-9 rounded-l-full rounded-r-none border border-r-0 border-blue-300/60 bg-blue-100 p-0 pl-2 text-primary-700 shadow-md hover:bg-blue-100 hover:text-primary-900"
              onClick={() => {
                openCommunications("messages");
              }}
              size="icon"
              title="Open communications"
              type="button"
              variant="ghost"
            >
              <MessagesSquare className="h-4 w-4" />
            </Button>
          ) : null}
        </Row>
      </Row>
    );
  };

  return render();
};
